/* eslint-disable react/prop-types */
import { Row, Radio } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CustomTreeSelect from '../../../fields/TreeSelect';
import LoadingSpinner from '../../../../lib/components/loadingSpinner';
import LineChartTimeline from './LineChart';
import { ComparisonDotPlot } from './ComparisonDotPlot'; // Change this line
import { dispatch } from '../../../../store';
import { handleStudentPerformanceRequest } from '../../../../modules/students/redux';
import { useSelector } from 'react-redux';
import { currentClassSelector } from '../../../../modules/classes/redux/selectors';
import { FormattedMessage } from 'react-intl';

const defaultClassValue = 'all';
const defaultTutorGroupValue = 'all';
const defaultSelectedSessionValue = 'all';

const CHART_TYPES = {
  TIMELINE: 'timeline',
  COMPARISON: 'comparison'
};

const useClassGroupTree = (classes) => {
  return useMemo(() => {
    const classTreeNodes = classes.map((classObj) => ({
      title: classObj.classData.title,
      value: classObj.classData.id,
      children: []
    }));

    return [
      {
        title: <FormattedMessage id="edit-student-profile.main.progress-section.allSessions"/>,
        value: defaultClassValue,
        children: []
      },
      ...classTreeNodes
    ];
  }, [classes]);
};

const useTutorGroupTree = (classes, selectedClass) => {
  return useMemo(() => {
    const selectedClassData = classes.find(classObj => classObj.classData.id === selectedClass);
    const tutorGroupTreeNodes = selectedClassData
      ? selectedClassData.tutorGroup?.map((tutorGroup) => ({
        title: tutorGroup.title,
        value: tutorGroup.id,
        children: []
      }))
      : [];

    return [
      {
        title: <FormattedMessage id="edit-student-profile.main.progress-section.allTutors" />,
        value: defaultSelectedSessionValue,
        children: []
      },
      ...tutorGroupTreeNodes
    ];
  }, [classes, selectedClass]);
};

const useTutorSessions = (tutorGroupSessions, selectedTutorGroup) => {
  return useMemo(() => {
    const selectedTutorGroupObj = tutorGroupSessions
      .find((tutorGroupObj) => tutorGroupObj.tutorGroup === selectedTutorGroup);

    const sessionTreeNodes = selectedTutorGroupObj ? selectedTutorGroupObj.sessions
      .map((session) => ({
        children: [],
        title: session.name,
        value: session.id
      })) : [];

    return [
      {
        title: <FormattedMessage id="edit-student-profile.main.progress-section.groupSessions" />,
        value: defaultSelectedSessionValue,
        children: []
      },
      ...sessionTreeNodes
    ];
  }, [tutorGroupSessions, selectedTutorGroup]);
};

const useStudentTree = (currentClass, studentId) => {
  return useMemo(() => {
    const studentsToCompare = (currentClass ? currentClass.students : [])
      .filter(student => student.id !== studentId)
      .map(student => ({
        title: `${student.firstName} ${student.lastName}`,
        value: student.id,
        children: []
      }));

    return [
      {
        title: <FormattedMessage id="edit-student-profile.main.progress-section.compareTo" />,
        value: 'none',
        children: []
      },
      ...studentsToCompare
    ];
  }, [currentClass, studentId]);
};

const PerformanceFilters = ({
  classesTree,
  tutorGroupsTree,
  sessionsTree,
  studentsTree,
  selectedClass,
  selectedTutorGroup,
  selectedSession,
  selectedTargetStudent,
  onTutorGroupChange,
  onSessionChange,
  onClassChange,
  onTargetStudentChange
}) => {
  return (
    <Row style={{ justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', gap: '8px' }}>
        <CustomTreeSelect
          treeData={classesTree}
          value={selectedClass}
          onChange={onClassChange}
        />
        <CustomTreeSelect
          treeData={tutorGroupsTree}
          value={selectedTutorGroup}
          onChange={onTutorGroupChange}
        />
        <CustomTreeSelect
          treeData={sessionsTree}
          value={selectedSession}
          onChange={onSessionChange}
        />
      </div>
      <CustomTreeSelect
        treeData={studentsTree}
        value={selectedTargetStudent}
        onChange={onTargetStudentChange}
        placeholder='Compare To'
        style={{ minWidth: 200 }}
      />
    </Row>
  );
};

export const StudentPerformance = ({
  firstName,
  studentId,
  isPerformanceLoading,
  performanceData,
  comparisonData,
  tutorGroupSessions,
  classWithTutorGroup
}) => {
  const [selectedClass, setSelectedClassValue] = useState(defaultClassValue);
  const [selectedTutorGroup, setSelectedTutorGroup] = useState(defaultTutorGroupValue);
  const [selectedSession, setSelectedSession] = useState(defaultSelectedSessionValue);
  const [selectedTargetStudent, setSelectedTargetStudent] = useState('none');
  const [chartType, setChartType] = useState(CHART_TYPES.TIMELINE);

  const currentClass = useSelector(currentClassSelector);

  const classesTree = useClassGroupTree(classWithTutorGroup);
  const tutorGroupsTree = useTutorGroupTree(classWithTutorGroup, selectedClass);
  const sessionsTree = useTutorSessions(tutorGroupSessions, selectedTutorGroup);
  const studentsTree = useStudentTree(currentClass, studentId);

  const handleClassChange = useCallback((value) => {
    setSelectedClassValue(value);
    setSelectedTutorGroup(defaultTutorGroupValue);
    setSelectedSession(defaultSelectedSessionValue);
  }, []);

  const handleTutorGroupChange = useCallback((value) => {
    setSelectedTutorGroup(value);
    setSelectedSession(defaultSelectedSessionValue);
  }, []);

  const handleTargetStudentChange = useCallback((value) => { // NEW HANDLER
    setSelectedTargetStudent(value);
  }, []);

  const handleChartTypeChange = useCallback((e) => {
    setChartType(e.target.value);
  }, []);

  useEffect(() => {
    const targetStudent = selectedTargetStudent !== 'none' ? selectedTargetStudent : undefined;
    dispatch(
      handleStudentPerformanceRequest({
        studentId,
        sessionId: selectedSession !== defaultSelectedSessionValue ? selectedSession : undefined,
        tutorGroupId: selectedTutorGroup !== defaultTutorGroupValue ? selectedTutorGroup : undefined,
        classId: selectedClass !== defaultClassValue ? selectedClass : undefined,
        targetStudentId: targetStudent
      })
    );
  }, [selectedSession, selectedTutorGroup, selectedClass, studentId, selectedTargetStudent]);

  return (
    <>
      <Row style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <strong>{firstName}'s progress:</strong>
        <Radio.Group
          value={chartType}
          onChange={handleChartTypeChange}
          buttonStyle='solid'
        >
          <Radio.Button value={CHART_TYPES.TIMELINE}><FormattedMessage id="edit-student-profile.main.progress-section.timeline" /></Radio.Button>
          <Radio.Button value={CHART_TYPES.COMPARISON}><FormattedMessage id="edit-student-profile.main.progress-section.comparison" /></Radio.Button>
        </Radio.Group>
      </Row>
      <PerformanceFilters
        classesTree={classesTree}
        tutorGroupsTree={tutorGroupsTree}
        sessionsTree={sessionsTree}
        studentsTree={studentsTree}
        selectedClass={selectedClass}
        selectedTutorGroup={selectedTutorGroup}
        selectedSession={selectedSession}
        selectedTargetStudent={selectedTargetStudent}
        onClassChange={handleClassChange}
        onTutorGroupChange={handleTutorGroupChange}
        onSessionChange={setSelectedSession}
        onTargetStudentChange={handleTargetStudentChange}
      />
      <LoadingSpinner spinning={isPerformanceLoading}>
        {chartType === CHART_TYPES.TIMELINE ? (
          <LineChartTimeline graphData={performanceData} />
        ) : (
          <ComparisonDotPlot
            comparisonData={comparisonData}
            studentName={firstName}
          />
        )}
      </LoadingSpinner>
    </>
  );
};
